import React, { useState } from 'react';
type Length = 60 | 75 | 90;

/** The No-Queue Session Clock, exactly as timed in every volume. */
const CLOCK: Record<Length, {
  warm: number;
  demo: number;
  station: number;
  match: number;
  huddle: number;
}> = {
  60: {
    warm: 8,
    demo: 4,
    station: 10,
    match: 15,
    huddle: 3
  },
  75: {
    warm: 10,
    demo: 5,
    station: 13,
    match: 18,
    huddle: 3
  },
  90: {
    warm: 12,
    demo: 5,
    station: 16,
    match: 22,
    huddle: 3
  }
};
const COLORS = {
  warm: '#D9467A',
  demo: '#F07F2A',
  station: ['#2D5BC4', '#3B6AD3', '#2550B0'],
  match: '#2F9E5B',
  huddle: '#7A4FC0'
};
const C = 120;
const R_OUT = 112;
const R_IN = 68;
const GAP = 1.4;
const pt = (r: number, deg: number): [number, number] => [C + r * Math.sin(deg * Math.PI / 180), C - r * Math.cos(deg * Math.PI / 180)];
function segment(a0: number, a1: number) {
  const s = a0 + GAP / 2;
  const e = a1 - GAP / 2;
  const large = e - s > 180 ? 1 : 0;
  const [x0, y0] = pt(R_OUT, s);
  const [x1, y1] = pt(R_OUT, e);
  const [x2, y2] = pt(R_IN, e);
  const [x3, y3] = pt(R_IN, s);
  return `M${x0.toFixed(2)} ${y0.toFixed(2)} A${R_OUT} ${R_OUT} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} L${x2.toFixed(2)} ${y2.toFixed(2)} A${R_IN} ${R_IN} 0 ${large} 0 ${x3.toFixed(2)} ${y3.toFixed(2)} Z`;
}
export function SessionClock() {
  const [length, setLength] = useState<Length>(60);
  const t = CLOCK[length];
  const parts = [{
    key: 'warm',
    min: t.warm,
    color: COLORS.warm
  }, {
    key: 'demo',
    min: t.demo,
    color: COLORS.demo
  }, {
    key: 's1',
    min: t.station,
    color: COLORS.station[0]
  }, {
    key: 's2',
    min: t.station,
    color: COLORS.station[1]
  }, {
    key: 's3',
    min: t.station,
    color: COLORS.station[2]
  }, {
    key: 'match',
    min: t.match,
    color: COLORS.match
  }, {
    key: 'huddle',
    min: t.huddle,
    color: COLORS.huddle
  }];
  let angle = 0;
  const arcs = parts.map(p => {
    const sweep = p.min / length * 360;
    const a0 = angle;
    angle += sweep;
    const mid = a0 + sweep / 2;
    return {
      ...p,
      d: segment(a0, a0 + sweep),
      label: pt((R_OUT + R_IN) / 2, mid)
    };
  });
  const legend = [{
    name: 'Warm-Up Game',
    min: `${t.warm} min`,
    color: COLORS.warm,
    note: 'Starts as the first players arrive. Latecomers join the game, not a queue.'
  }, {
    name: 'Focus Demo',
    min: `${t.demo} min`,
    color: COLORS.demo,
    note: 'Tonight’s skill, shown in a few minutes.'
  }, {
    name: 'Three stations',
    min: `3 × ${t.station} min`,
    color: COLORS.station[0],
    note: 'Batting, bowling and fielding. Groups rotate on one signal.'
  }, {
    name: 'Match Game',
    min: `${t.match} min`,
    color: COLORS.match,
    note: 'Tonight’s skill inside a game with rules and a score.'
  }, {
    name: 'Huddle',
    min: `${t.huddle} min`,
    color: COLORS.huddle,
    note: 'One question before everyone heads home.'
  }];
  return <div className="flex h-full flex-col rounded-3xl bg-cream-light p-5 text-left text-ink shadow-page sm:p-7" data-id="element-299" data-mp="2ffx6jzsyjhvy">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-300" data-mp="l3p7ca9lseud">
        <div data-id="element-301" data-mp="l5tjtglkim69">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-grass-deep" data-id="element-302" data-mp="l61ssxekl7c6">Time</p>
          <h3 className="font-display text-xl font-extrabold leading-tight sm:text-2xl" data-id="element-303" data-mp="31q5l0u33bmzu">The No-Queue Session Clock</h3>
        </div>
        <div className="flex rounded-full bg-cream-dark/70 p-1" role="group" aria-label="Session length" data-id="element-304" data-mp="2n20ytg4twtcb">
          {([60, 75, 90] as Length[]).map(l => <button key={l} type="button" onClick={() => setLength(l)} aria-pressed={length === l} className={`min-h-[44px] min-w-[52px] rounded-full px-3 text-sm font-bold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-grass-deep ${length === l ? 'bg-ink text-cream' : 'text-ink-soft hover:text-ink'}`} data-id="element-305" data-mp="2rxzkgvyrvx91">
              {l}′
            </button>)}
        </div>
      </div>

      <div className="mt-5 grid items-center gap-6 sm:grid-cols-[minmax(0,240px)_1fr]" data-id="element-306" data-mp="7ad5rvsxrkys">
        <svg viewBox="0 0 240 240" className="mx-auto w-full max-w-[240px]" role="img" aria-label={`A ${length}-minute session: warm-up ${t.warm}, demo ${t.demo}, three stations of ${t.station}, match game ${t.match}, huddle ${t.huddle} minutes`} data-id="element-307" data-mp="23drm0vpcu2cx">
          {arcs.map(a => <g key={a.key} data-id="element-308" data-mp="1pdgudqg80jqp">
              <path d={a.d} fill={a.color} data-id="element-309" data-mp="1pe9rs0tahww6" />
              <text x={a.label[0]} y={a.label[1] + 4.5} textAnchor="middle" fill="#FFFFFF" fontFamily="'Inter Tight', Inter, sans-serif" fontWeight="800" fontSize="13" data-id="element-310" data-mp="1pfkxi66apepx">
                {a.min}′
              </text>
            </g>)}
          <text x="120" y="126" textAnchor="middle" fill="#0F1B3D" fontFamily="'Inter Tight', Inter, sans-serif" fontWeight="900" fontSize="50" letterSpacing="-1.5" data-id="element-311" data-mp="16oa6zq4hf3yk">
            {length}
          </text>
          <text x="120" y="148" textAnchor="middle" fill="#47526E" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="13" letterSpacing="1.5" data-id="element-312" data-mp="wq4ajo1g2q13">
            MIN
          </text>
        </svg>

        <ol className="grid gap-2.5" data-id="element-313" data-mp="rinwuuhp1rj4">
          {legend.map(l => <li key={l.name} className="flex gap-3" data-id="element-314" data-mp="wiincwxk4f3n">
              <span className="mt-1 h-3 w-3 flex-none rounded-[3px]" style={{
            background: l.color
          }} aria-hidden="true" data-id="element-315" data-mp="wgz7h7ftc62e" />
              <div data-id="element-316" data-mp="3uel6le72k5p">
                <p className="text-sm font-bold leading-snug" data-id="element-317" data-mp="3uwkxs02fgid">
                  {l.name} <span className="font-semibold text-ink-soft" data-id="element-318" data-mp="8thcz455ytvq">· {l.min}</span>
                </p>
                <p className="text-[13px] leading-snug text-ink-soft" data-id="element-319" data-mp="dskisqsyx44r">{l.note}</p>
              </div>
            </li>)}
        </ol>
      </div>
    </div>;
}